'use strict'

module.exports = createClient

const httpErrors = require('http-errors')
const axios = require('axios')
const axiosRetry = require('axios-retry')

const IS_ADMIN = Symbol('is-admin-client')

function Client (opts) {
  const headers = opts.headers || {}
  const jwt = opts.jwt

  if (jwt) {
    headers.authorization = `Bearer ${jwt}`
  }
  this.axios = setupAxios(opts)

  Object.assign(this, {
    logger: opts.logger || { info () {}, error () {} },
    headers,
    requestId: opts.requestId || headers['request-id'],
    host: opts.host,
    agent: opts.agent,
    jwt,
    retries: opts.retries
  })
  this[IS_ADMIN] = false
}

Object.assign(Client.prototype, {
  createAuthenticatedClient,
  createAdminClient,
  searchUser,
  request,
  sendRequest
})

function createClient (opts) {
  return new Client(opts || {})
}

function createAuthenticatedClient (ident, jwt) {
  if (this[IS_ADMIN]) {
    throw new Error(`
    Cannot create an authenticated client from an admin client.
    `.trim())
  }

  const headers = Object.assign({}, this.headers || {}, { bearer: ident })

  return new Client({
    headers,
    requestId: this.requestId || headers['request-id'],
    logger: this.logger,
    host: this.host,
    agent: this.agent,
    jwt
  })
}

function createAdminClient (jwt) {
  const headers = Object.assign({}, this.headers)
  headers.bearer = process.env.NPM_STAFF || 'npm-support'

  const client = new Client({
    headers,
    requestId: this.requestId || headers['request-id'],
    logger: this.logger,
    host: this.host,
    agent: this.agent,
    jwt
  })
  client[IS_ADMIN] = true
  return client
}

function request (strings) {
  const out = [strings[0]]
  for (let i = 1; i < strings.length; ++i) {
    out.push(encodeURIComponent(arguments[i]))
    out.push(strings[i])
  }

  const bits = out.join('').trim().split(/\s+/)

  return (client, headers, body, qs) => {
    return Promise.resolve(this.sendRequest(bits[0], bits[1], headers, body, qs))
  }
}

async function sendRequest (method, url, headers, body, qs) {
  const start = Date.now()

  headers = Object.assign(
    headers || {},
    this.headers || {},
    this.requestId ? { 'request-id': this.requestId } : {},
    { 'downstream-request-id': Math.random().toString(16).substr(2, 4) }
  )

  // axios does not like undefined as header value
  // To avoid changing the tests, the headers are filtered here
  for (const header of Object.keys(headers)) {
    if (headers[header] === undefined) {
      delete headers[header]
    }
  }

  const bearer = (headers && headers.bearer) || '(anon)'
  this.logger.info(`audit(id=${headers['request-id']}~${headers['downstream-request-id']}) ${method} ${url} (as ${bearer})`)

  let success = true
  try {
    const resp = await this.axios({
      method,
      url,
      headers,
      data: body,
      params: typeof (qs) === 'object' ? qs : {}
    })
    return {
      statusCode: resp.status,
      headers: resp.headers,
      body: resp.data
    }
  } catch (e) {
    success = false
    if (e.response === undefined) {
      throw e
    }
    const statusCode = e.response.status
    const body = e.response.data
    const headers = e.response.headers

    if (statusCode > 499) {
      const { authorization, ...headersToLog } = headers
      this.logger.error({ message: 'Error from audit-log-server', requestHeaders: headersToLog, originalStack: body && body.stack })
      if (body && body.stack) {
        this.logger.error('stack from remote server:')
        this.logger.error(body.stack)
      }
    }
    if (statusCode > 399) {
      throw httpErrors(statusCode, body.message, {
        originalStack: body && body.stack,
        headers,
        errors: body && body.errors
      })
    }
  } finally {
    process.emit('metric', {
      name: 'audit-log-server-latency',
      value: Date.now() - start,
      result: success ? 'success' : 'failure'
    })
  }
}

function setupAxios ({
  maxBodyLength = 2 * 1024 * 1024, // Two MB
  requestTimeout = 30000,
  host,
  retries = null,
  logger = { info () {}, error () {} }
} = {}) {
  const axiosInstance = axios.create({
    baseURL: host,
    timeout: requestTimeout,
    maxContentLength: maxBodyLength,
    maxBodyLength
  })

  if (retries) {
    axiosRetry(axiosInstance, {
      retries,
      retryCondition: (error) => {
        if (error.response && error.response.status >= 500) {
          logger.error(`Retry, status: ${error.response.status}`)
          process.emit('metric', {
            name: 'audit-log-retry-5xx',
            count: 1
          })
          return true
        }
        return false
      }
    })
  }
  return axiosInstance
}

async function searchUser (filter, paginate = { perPage: 20 }) {
  const qs = { perPage: paginate.perPage, page: paginate.page }
  return this.request`POST /search/user`(this, {}, filter, qs)
}
